import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";
import BrandLogo from "../../components/common/BrandLogo";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useAppDispatch, useAppSelector } from "../../store";
import { resetPassword, forgotPassword, clearError } from "../../features/auth/authSlice";
import "../../styles/auth.scss";
import Swal from "sweetalert2";

interface ResetPasswordFormInputs {
    code: string;
    newPassword: string;
    confirmPassword: string;
}

const schema = yup.object().shape({
    code: yup
        .string()
        .required("Verification code is required")
        .matches(/^\d{6}$/, "Code must be exactly 6 digits"),
    newPassword: yup
        .string()
        .required("New password is required")
        .min(8, "Password must be at least 8 characters")
        .matches(
            /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[\W_])/,
            "Password must contain uppercase, lowercase, number and special character"
        ),
    confirmPassword: yup
        .string()
        .required("Please confirm your password")
        .oneOf([yup.ref("newPassword")], "Passwords must match"),
});

const ResetPasswordPage: React.FC = () => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const { isLoading, error } = useAppSelector((state) => state.auth);

    const [email, setEmail] = useState<string>("");
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [countdown, setCountdown] = useState(180);
    const [resendDisabled, setResendDisabled] = useState(false);

    useEffect(() => {
        const stateEmail = location.state?.email;
        if (stateEmail) {
            setEmail(stateEmail);
        } else {
            // If no email, go back to forgot password
            navigate("/forgot-password");
        }

        return () => {
            dispatch(clearError());
        };
    }, [location, navigate, dispatch]);

    useEffect(() => {
        let timer: NodeJS.Timeout;
        if (countdown > 0) {
            timer = setTimeout(() => setCountdown(countdown - 1), 1000);
            setResendDisabled(true);
        } else {
            setResendDisabled(false);
        }
        return () => clearTimeout(timer);
    }, [countdown]);

    const handleResend = async () => {
        if (!email) return;

        setResendDisabled(true);
        setCountdown(180); // 180 seconds cooldown

        const resultAction = await dispatch(forgotPassword(email));
        if (forgotPassword.fulfilled.match(resultAction)) {
            Swal.fire({
                icon: "success",
                title: "New Code Sent!",
                html: `<p style="margin: 0; font-size: 15px; color: #64748b;">We've sent a fresh verification code to</p><p style="margin: 8px 0 0 0; font-size: 15px; color: #1e293b; font-weight: 600;">${email}</p>`,
                timer: 3000,
                showConfirmButton: false,
                customClass: {
                    popup: 'swal-modern-popup',
                    title: 'swal-modern-title',
                    htmlContainer: 'swal-modern-html'
                }
            });
        }
    };

    const {
        register,
        handleSubmit,
        formState: { errors },
    } = useForm<ResetPasswordFormInputs>({
        resolver: yupResolver(schema),
    });

    const onSubmit = async (data: ResetPasswordFormInputs) => {
        if (!email) return;

        const resultAction = await dispatch(resetPassword({
            email,
            code: data.code,
            new_password: data.newPassword
        }));

        if (resetPassword.fulfilled.match(resultAction)) {
            Swal.fire({
                icon: "success",
                title: "Password Reset Successfully!",
                html: `<p style="margin: 0; font-size: 15px; color: #64748b;">Your password has been updated. Redirecting to login...</p>`,
                timer: 2500,
                showConfirmButton: false,
                customClass: {
                    popup: 'swal-modern-popup',
                    title: 'swal-modern-title',
                    htmlContainer: 'swal-modern-html'
                }
            }).then(() => {
                navigate("/login");
            });
        }
    };

    const togglePasswordVisibility = () => {
        setShowPassword(!showPassword);
    };

    const toggleConfirmPasswordVisibility = () => {
        setShowConfirmPassword(!showConfirmPassword);
    };

    return (
        <div className="auth-page reset-password-page">
            <div className="gradient-orb"></div>
            <div className="auth-container">
                <div className="auth-card-wrapper">
                    <div className="auth-card">
                        <div className="auth-logo">
                            <BrandLogo height={28} />
                        </div>

                        <h2 className="form-title">Reset Password</h2>
                        <p className="form-subtitle">
                            Enter the code sent to <strong>{email}</strong> and your new password.
                        </p>

                        {error && (
                            <div className="alert" role="alert">
                                {error}
                            </div>
                        )}

                        <form className="auth-form verification-form" onSubmit={handleSubmit(onSubmit)}>
                            <div className="form-group">
                                <label className="code-input-label">Enter Verification Code</label>
                                <div className="code-input-wrapper">
                                    <input
                                        type="text"
                                        className={`verification-code-input ${errors.code ? "is-invalid" : ""}`}
                                        id="code"
                                        placeholder="000000"
                                        maxLength={6}
                                        {...register("code")}
                                        autoComplete="off"
                                        inputMode="numeric"
                                        pattern="[0-9]*"
                                    />
                                    {errors.code && (
                                        <div className="error-message">{errors.code.message}</div>
                                    )}
                                </div>
                            </div>

                            <div className="form-group">
                                <div className="input-group">
                                    <input
                                        type={showPassword ? "text" : "password"}
                                        className={`form-control ${errors.newPassword ? "is-invalid" : ""}`}
                                        id="newPassword"
                                        placeholder="New Password"
                                        {...register("newPassword")}
                                    />
                                    <button
                                        type="button"
                                        className="password-toggle"
                                        onClick={togglePasswordVisibility}
                                    >
                                        <i className={`bi ${showPassword ? "bi-eye-slash" : "bi-eye"}`}></i>
                                    </button>
                                    {errors.newPassword && (
                                        <div className="error-message">{errors.newPassword.message}</div>
                                    )}
                                </div>
                            </div>

                            <div className="form-group">
                                <div className="input-group">
                                    <input
                                        type={showConfirmPassword ? "text" : "password"}
                                        className={`form-control ${errors.confirmPassword ? "is-invalid" : ""}`}
                                        id="confirmPassword"
                                        placeholder="Confirm New Password"
                                        {...register("confirmPassword")}
                                    />
                                    <button
                                        type="button"
                                        className="password-toggle"
                                        onClick={toggleConfirmPasswordVisibility}
                                    >
                                        <i className={`bi ${showConfirmPassword ? "bi-eye-slash" : "bi-eye"}`}></i>
                                    </button>
                                    {errors.confirmPassword && (
                                        <div className="error-message">{errors.confirmPassword.message}</div>
                                    )}
                                </div>
                            </div>

                            <button
                                type="submit"
                                className={`verify-button ${isLoading ? "loading" : ""}`}
                                disabled={isLoading}
                            >
                                {isLoading ? (
                                    <>
                                        <span className="spinner" role="status" aria-hidden="true"></span>
                                        Updating Password...
                                    </>
                                ) : (
                                    <>
                                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                            <path d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                        </svg>
                                        Update Password
                                    </>
                                )}
                            </button>

                            <div className="resend-section">
                                <button
                                    type="button"
                                    className={`resend-button ${resendDisabled ? "disabled" : ""}`}
                                    onClick={handleResend}
                                    disabled={resendDisabled || isLoading}
                                >
                                    {resendDisabled ? (
                                        <>
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <circle cx="12" cy="12" r="10" />
                                                <polyline points="12 6 12 12 16 14" />
                                            </svg>
                                            Resend in {Math.floor(countdown / 60)}:{(countdown % 60).toString().padStart(2, '0')}
                                        </>
                                    ) : (
                                        <>
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8" />
                                                <path d="M21 3v5h-5" />
                                                <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16" />
                                                <path d="M3 21v-5h5" />
                                            </svg>
                                            Resend Code
                                        </>
                                    )}
                                </button>
                            </div>

                            <div className="back-link-wrapper">
                                <button
                                    type="button"
                                    className="back-link"
                                    onClick={() => navigate("/login")}
                                >
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M19 12H5M12 19l-7-7 7-7" />
                                    </svg>
                                    Back to Login
                                </button>
                            </div>
                        </form>
                    </div>

                    <div className="copyright">
                        <div className="copyright-content">
                            <span>&copy; {new Date().getFullYear()} Meylo</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ResetPasswordPage;
